import React from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { round } from 'lodash'
import { Card } from '@mui/material'
import { MONTH } from '../StatisticsUtil'
import { formatMilliers } from 'common/utils/StringUtil'

const fmt = (v) => formatMilliers(round(Number(v), 1), true)

// Affiche la valeur en label uniquement sur le dernier point (mois en cours)
const withCurrentMonthLabel = (values) => values.map((v, i) => (
    i === values.length - 1 && Number.isFinite(v)
        ? { value: v, label: { show: true, position: 'top', formatter: ({ value }) => fmt(value) } }
        : v
))

// Ligne de moyenne (sans label : la valeur est affichée dans la légende)
const averageMarkLine = () => ({
    symbol: 'none',
    data: [{ type: 'average', name: 'Moyenne' }],
    label: { show: false },
})

// Moyenne des valeurs finies d'une série
const average = (values) => {
    const nums = values.filter(Number.isFinite)
    return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null
}

const TimesBar = ({ chartData = [], loading = false }) => {
    const avgByName = {
        'Temps de Réponse': average(chartData.map(d => d.answerTime)),
        'Temps de Résolution': average(chartData.map(d => d.solveTime)),
        'Temps de MEP': average(chartData.map(d => d.mepTime)),
    }

    const options = {
        title: {
            text: 'Temps de réponse et de résolution',
            left: 'center',
            top: 10,
        },
        grid: {
            top: 50,
            bottom: 60,
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const header = params[0]?.axisValue ?? ''
                const lines = params.map(p => `${p.marker}${p.seriesName} : ${Number.isFinite(p.value) ? fmt(p.value) : '-'} jours ouvrés`)
                return [header, ...lines].join('<br/>')
            },
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: {
                    show: true,
                    title: 'Télécharger l\'image',
                    name: 'Recette',
                    backgroundColor: 'white',
                    pixelRatio: 2,
                },
            },
            right: '10px',
            top: '10px',
        },
        xAxis: {
            type: 'category',
            data: chartData.map(d => {
                const splitted = d.date.split('-')
                return `${MONTH[parseInt(splitted[0])]}`
            }),
            axisLabel: { interval: 0 },
        },
        yAxis: {
            type: 'value',
            name: 'Temps en jour ouvré',
            nameRotate: 90,
            nameLocation: 'center',
            nameGap: 40,
            axisLabel: { formatter: (v) => formatMilliers(v) },
        },
        legend: {
            left: 'center',
            type: 'scroll',
            width: '90%',
            itemGap: 20,
            bottom: '2%',
            formatter: (name) => {
                const a = avgByName[name]
                return Number.isFinite(a) ? `${name} : ${fmt(a)} j (moy.)` : name
            },
        },
        series: [
            {
                name: 'Temps de Réponse',
                type: 'bar',
                data: withCurrentMonthLabel(chartData.map(d => d.answerTime)),
                markLine: averageMarkLine(),
            },
            {
                name: 'Temps de Résolution',
                type: 'bar',
                data: withCurrentMonthLabel(chartData.map(d => d.solveTime)),
                markLine: averageMarkLine(),
            },
            {
                name: 'Temps de MEP',
                type: 'bar',
                data: withCurrentMonthLabel(chartData.map(d => d.mepTime)),
                markLine: averageMarkLine(),
            },
        ],
    }

    return (
        <Card>
            <ReactECharts
                option={options}
                notMerge={true}
                lazyUpdate={true}
                showLoading={loading}
                style={{ height: '300px' }}
            />
        </Card>
    )
}

TimesBar.propTypes = {
    loading: PropTypes.bool,
    chartData: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.string,
        answerTime: PropTypes.number,
        solveTime: PropTypes.number,
        mepTime: PropTypes.number,
    })),
}

export default TimesBar
